<script>
import date from 'locutus/php/datetime/date'
import defaults from 'lodash/defaults'
import {
  fetchClient,
  createClient,
  updateClient,
  fetchClientGrants,
  fetchClientGrant,
  createClientGrant,
  updateClientGrant,
  deleteClientGrant,
} from '@apis/admin/oauth2'
import { mapState } from 'vuex'
import fromPairs from 'lodash/fromPairs'
import filter from 'lodash/filter'
import map from 'lodash/map'

export default {
  props: {
    client_seq: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      params: {},
      info: null,
      client_grants: [],
      grant_params: null,
      loading: false,
      active_tab: 'info',
    }
  },
  computed: {
    ...mapState('common', {
      constants: (state) => state.constants,
    }),
    c_selectable_grants() {
      const client_grants = map(this.client_grants, ({ grant }) => grant)
      return filter(
        this.constants.oauth2.grant,
        ([grant]) => client_grants.indexOf(grant) === -1
      )
    },
  },
  created() {
    this.init()
  },
  methods: {
    init() {
      this.loading = true
      Promise.all([this.loadClient()])
        .then(() => this.loadClientGrants())
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    async loadClient() {
      const { client_info } = await fetchClient(this.client_seq)
      this.params = defaults(client_info, {
        member_seq: this.$route.params.member_seq,
        redirect_uri: '',
        client_id: '',
        client_secret: '',
        seq: null,
        regdate: null,
      })
      this.info = client_info
    },
    async loadClientGrants() {
      if (!this.info) return []

      const { data } = await fetchClientGrants(this.info.seq)

      this.client_grants = data
    },
    async loadClientGrant(grant_seq) {
      const { data } = await fetchClientGrant(this.info.seq, grant_seq)

      this.grant_params = data
    },
    handleSubmit() {
      const createOrUpdate = this.params.seq ? updateClient : createClient
      const confirm_message = this.params.seq
        ? this.$t('admin.msg_save')
        : this.$t('admin.msg_enroll')
      const success_message = this.params.seq
        ? this.$t('oauth2_client_info.msg_modified')
        : this.$t('oauth2_client_info.msg_registered')

      this.loading = true
      this.$confirm(confirm_message, this.$t('admin.modal_confirm'))
        .then(() => createOrUpdate(this.params))
        .then(({ seq }) => this.$emit('submit', seq))
        .then(() => this.$toasted.success(success_message))
        .catch((e) => (e ? this.$toasted.error(e.message) : null))
        .finally(() => {
          this.loading = false
        })
    },
    handleAddGrant() {
      this.client_grants.push({
        list_order: 0,
        client_seq: this.info.seq,
        grant: undefined,
      })
    },
    async handleDeleteGrant(idx) {
      this.loading = true
      try {
        if (this.client_grants[idx].seq) {
          if (
            !(await this.$confirm(
              this.$t('admin.confirm_delete'),
              this.$t('admin.modal_confirm'),
              {
                type: 'warning',
              }
            ).catch(() => false))
          )
            return

          await deleteClientGrant(this.info.seq, this.client_grants[idx].seq)
        }

        this.$notify({
          title: this.$t('toasted_msg.title'),
          message: this.$t('admin.msg_delete'),
          type: 'success',
        })
        this.client_grants.splice(idx, 1)
      } catch (e) {
        this.$toasted.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handleSubmitGrant(idx) {
      this.loading = true
      const params = this.client_grants[idx]
      ;(params.seq ? updateClientGrant : createClientGrant)(params)
        .then(({ seq }) => fetchClientGrant(this.info.seq, seq))
        .then(({ grant_info }) => {
          this.$notify({
            title: this.$t('toasted_msg.title'),
            message: params.seq
              ? this.$t('admin.msg_save_success')
              : this.$t('admin.msg_enroll_success'),
            type: 'success',
          })
          this.$set(this.client_grants, idx, { ...grant_info })
        })
        .catch((e) => this.$toasted.error(e.message))
        .finally(() => {
          this.loading = false
        })
    },
    date,
    fromPairs,
  },
}
</script>

<template>
  <el-tabs v-model="active_tab" :stretch="true">
    <el-tab-pane :label="$t('admin.default_info')" name="info">
      <form
        v-loading="loading"
        action="./"
        method="get"
        @submit.prevent="handleSubmit"
      >
        <table cellspacing="0" cellpadding="0" :class="$style.rowTable">
          <colgroup>
            <col width="20%" />
            <col width="30%" />
            <col width="20%" />
            <col width="30%" />
          </colgroup>
          <tbody>
            <tr>
              <th scope="row"
                ><div>{{ $t('oauth2_client_info.name') }}</div></th
              >
              <td colspan="3"
                ><input
                  v-model="params.title"
                  type="text"
                  :class="[$style.inputTypeText, $style.fullWidth]"
              /></td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('oauth2_client_info.id') }}</div></th
              >
              <td colspan="3">{{
                params.seq
                  ? params.client_id
                  : $t('oauth2_client_info.automatic_generation')
              }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('oauth2_client_info.secret') }}</div></th
              >
              <td colspan="3">{{
                params.seq
                  ? params.client_secret
                  : $t('oauth2_client_info.automatic_generation')
              }}</td>
            </tr>
            <tr>
              <th scope="row"
                ><div>{{ $t('oauth2_client_info.url') }}</div></th
              >
              <td colspan="3"
                ><textarea
                  v-model="params.redirect_uri"
                  :class="[$style.inputTypeTextArea, $style.fullWidth]"
                ></textarea
              ></td>
            </tr>
            <tr>
              <th colspan="4" :class="$style.button">
                <span :class="[$style.button, $style.black]"
                  ><input
                    type="submit"
                    :value="params.seq ? $t('admin.save') : $t('admin.enroll')"
                /></span>
              </th>
            </tr>
          </tbody>
        </table>
      </form>
    </el-tab-pane>
    <el-tab-pane
      :disabled="!info || !info.seq"
      :label="$t('oauth2_client_info.right')"
      name="GRANT"
    >
      <table
        v-if="info && info.seq"
        cellspacing="0"
        cellpadding="0"
        :class="$style.colTable"
      >
        <thead>
          <tr>
            <th scope="col">{{ $t('oauth2_client_info.right') }}</th>
            <th scope="col">
              <div :class="$style.tRight">
                <a
                  href="#"
                  :class="[$style.black, $style.button]"
                  @click="handleAddGrant"
                  ><span>{{ $t('admin.add') }}</span></a
                >
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(grant, idx) in client_grants"
            :key="grant.seq ? `${grant.seq}` : `create-${idx}`"
          >
            <td :class="$style.wide">
              <select v-model="grant.grant" :disabled="grant.seq">
                <option :value="undefined">{{
                  $t('oauth2_client_info.choice')
                }}</option>
                <option
                  v-for="val in constants.oauth2.grant"
                  :key="val[0]"
                  :value="val[0]"
                >
                  {{ val[1] }}
                </option>
              </select>
            </td>
            <td :class="[$style.tRight, $style.nowrap]">
              <a
                v-if="!grant.seq"
                href="#"
                :class="[$style.black, $style.button]"
                @click="handleSubmitGrant(idx)"
                ><span>{{ $t('admin.enroll') }}</span></a
              >
              {{ ' ' }}
              <a
                href="#"
                :class="[$style.red, $style.button]"
                @click="handleDeleteGrant(idx)"
                ><span>{{ $t('admin.delete') }}</span></a
              >
            </td>
          </tr>
          <tr v-if="!client_grants.length">
            <td :class="$style.tCenter" colspan="3">{{
              $t('oauth2_client_info.msg_not_egistered')
            }}</td>
          </tr>
        </tbody>
      </table>
    </el-tab-pane>
  </el-tabs>
</template>

<style lang="scss" module>
@import '@src/design/default.scss';
@import '@src/design/admin.scss';
@import '@src/design/font.scss';
@import '@src/design/button.scss';
</style>
